import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { getBackendUrl } from '../../lib/env';
import { getShopUrl } from '../../lib/domains';

const API_URL = getBackendUrl();

function ensureMeta(name, content, attr = 'name') {
  if (!content) return;
  const selector = attr === 'property' ? `meta[property="${name}"]` : `meta[name="${name}"]`;
  let tag = document.querySelector(selector);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function canonicalPath(categorySlug, locationSlug) {
  if (categorySlug) return `/directory/category/${categorySlug}`;
  if (locationSlug) return `/directory/location/${locationSlug}`;
  return '/directory';
}

export default function MarketplaceDirectory() {
  const params = useParams();
  const navigate = useNavigate();
  const categorySlug = params.categorySlug || '';
  const locationSlug = params.locationSlug || '';
  const [search, setSearch] = useState('');
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API_URL}/api/public/marketplace`, {
      params: {
        q: search,
        category: categorySlug,
        location: locationSlug,
        limit: 60,
      },
    }).then((response) => {
      if (!cancelled) setData(response.data || null);
    }).catch(() => {
      if (!cancelled) setData(null);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [search, categorySlug, locationSlug]);

  useEffect(() => {
    const activeCategory = data?.categories?.find(cat => cat.slug === categorySlug);
    const activeLocation = data?.locations?.find(loc => loc.slug === locationSlug);
    const title = activeCategory
      ? `${activeCategory.name} Stores | Jjuma Marketplace`
      : activeLocation
        ? `${activeLocation.name} Stores | Jjuma Marketplace`
        : 'Jjuma Marketplace | Active Stores';
    const description = activeCategory
      ? `Browse active Jjuma stores in ${activeCategory.name}.`
      : activeLocation
        ? `Browse active Jjuma stores in ${activeLocation.name}.`
        : 'Discover active Jjuma stores, featured businesses, and shop links.';
    const canonical = getShopUrl(canonicalPath(categorySlug, locationSlug));
    const image = data?.featured_stores?.[0]?.cover_image_url || data?.featured_stores?.[0]?.logo_url || '';
    document.title = title;
    ensureMeta('description', description);
    ensureMeta('keywords', ['marketplace', 'stores', activeCategory?.name, activeLocation?.name].filter(Boolean).join(', '));
    ensureMeta('og:title', title, 'property');
    ensureMeta('og:description', description, 'property');
    ensureMeta('og:url', canonical, 'property');
    ensureMeta('og:type', 'website', 'property');
    ensureMeta('og:site_name', 'Jjuma Pay', 'property');
    ensureMeta('og:image', image, 'property');
    ensureMeta('og:image:alt', title, 'property');
    ensureMeta('twitter:card', image ? 'summary_large_image' : 'summary');
    ensureMeta('twitter:title', title);
    ensureMeta('twitter:description', description);
    ensureMeta('twitter:image', image);
    ensureMeta('twitter:url', canonical);
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonical);
  }, [data, categorySlug, locationSlug]);

  const stores = data?.stores || [];
  const featuredStores = data?.featured_stores || [];
  const regularStores = stores.filter(store => !store.marketplace_featured);
  const categories = data?.categories || [];
  const locations = data?.locations || [];

  const activeFilterLabel = useMemo(() => {
    if (categorySlug) {
      return categories.find(item => item.slug === categorySlug)?.name || categorySlug;
    }
    if (locationSlug) {
      return locations.find(item => item.slug === locationSlug)?.name || locationSlug;
    }
    return '';
  }, [categorySlug, locationSlug, categories, locations]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  const sharePage = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: document.title,
          text: 'Browse active Jjuma stores.',
          url: window.location.href,
        });
        return;
      } catch {}
    }
    copyLink();
  };

  const openStore = (store) => {
    window.location.href = getShopUrl(`/${store.username}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="w-8 h-8 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.08),_transparent_34%),linear-gradient(180deg,#f8fafc_0%,#eff6ff_100%)]">
      <div className="mx-auto max-w-6xl px-4 py-6 space-y-5">
        <Card className="border border-slate-200 bg-white/90 shadow-sm rounded-[22px]">
          <CardContent className="p-4 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="space-y-1">
              <p className="text-xs uppercase tracking-[0.18em] text-emerald-700 font-semibold">Jjuma Marketplace</p>
              <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {activeFilterLabel ? `${activeFilterLabel} Stores` : 'Discover active stores'}
              </h1>
              <p className="text-sm text-slate-500">
                Search active stores, featured businesses and local sellers on Jjuma Pay.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" className="border-slate-200 bg-white" onClick={copyLink}>
                {copied ? 'Copied' : 'Copy link'}
              </Button>
              <Button type="button" className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={sharePage}>
                Share
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200 bg-white shadow-sm rounded-[24px]">
          <CardContent className="p-4 space-y-4">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search stores, categories or locations"
              className="h-12"
            />
            <div className="space-y-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 mb-2">Categories</p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={!categorySlug && !locationSlug ? 'default' : 'outline'}
                    className={!categorySlug && !locationSlug ? 'bg-emerald-700 hover:bg-emerald-800 text-white' : 'border-slate-200 bg-white'}
                    onClick={() => navigate('/directory')}
                  >
                    All stores
                  </Button>
                  {categories.map((category) => (
                    <Button
                      key={category.slug}
                      type="button"
                      size="sm"
                      variant={categorySlug === category.slug ? 'default' : 'outline'}
                      className={categorySlug === category.slug ? 'bg-emerald-700 hover:bg-emerald-800 text-white' : 'border-slate-200 bg-white'}
                      onClick={() => navigate(`/directory/category/${category.slug}`)}
                    >
                      {category.name}
                    </Button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 mb-2">Locations</p>
                <div className="flex flex-wrap gap-2">
                  {locations.map((locationItem) => (
                    <Button
                      key={locationItem.slug}
                      type="button"
                      size="sm"
                      variant={locationSlug === locationItem.slug ? 'default' : 'outline'}
                      className={locationSlug === locationItem.slug ? 'bg-emerald-700 hover:bg-emerald-800 text-white' : 'border-slate-200 bg-white'}
                      onClick={() => navigate(`/directory/location/${locationItem.slug}`)}
                    >
                      {locationItem.name}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {featuredStores.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Featured stores</h2>
              <p className="text-xs text-slate-500">{featuredStores.length} featured</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {featuredStores.map((store) => (
                <StoreCard key={store.id} store={store} onOpen={() => openStore(store)} />
              ))}
            </div>
          </div>
        )}

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>All stores</h2>
            <p className="text-xs text-slate-500">{regularStores.length} results</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {regularStores.map((store) => (
              <StoreCard key={store.id} store={store} onOpen={() => openStore(store)} />
            ))}
          </div>
          {regularStores.length === 0 && featuredStores.length === 0 && (
            <Card className="border border-slate-200 bg-white rounded-2xl">
              <CardContent className="p-8 text-center text-slate-500">
                No stores match your search right now.
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function StoreCard({ store, onOpen }) {
  return (
    <Card className="h-full overflow-hidden border border-slate-200 bg-white shadow-sm rounded-[24px]">
      <div className="h-40 bg-slate-100 relative">
        {store.cover_image_url ? <img src={store.cover_image_url} alt={store.store_name} className="h-full w-full object-cover" /> : null}
        {store.marketplace_featured ? (
          <div className="absolute top-3 left-3 rounded-full bg-amber-500 px-3 py-1 text-[11px] font-semibold text-white shadow">
            Featured
          </div>
        ) : null}
      </div>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl overflow-hidden border border-slate-200 bg-slate-50 shrink-0">
            {store.logo_url ? <img src={store.logo_url} alt={store.store_name} className="h-full w-full object-cover" /> : <div className="h-full w-full flex items-center justify-center font-semibold text-emerald-700">{store.store_name?.charAt(0)?.toUpperCase() || 'S'}</div>}
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-slate-900 line-clamp-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{store.store_name}</h3>
            <p className="text-xs text-slate-500 line-clamp-1">{store.marketplace_location || store.location || store.business_category || 'Jjuma store'}</p>
          </div>
        </div>
        <p className="text-sm text-slate-600 line-clamp-3">{store.marketplace_description || store.short_description || 'Store on Jjuma Pay'}</p>
        <div className="flex flex-wrap gap-2 text-[11px] text-slate-500">
          {store.marketplace_category ? <span className="rounded-full bg-slate-100 px-2.5 py-1">{store.marketplace_category}</span> : null}
          {store.currency ? <span className="rounded-full bg-slate-100 px-2.5 py-1">{store.currency}</span> : null}
          {store.rating ? <span className="rounded-full bg-slate-100 px-2.5 py-1">Rating {Number(store.rating).toFixed(1)}</span> : null}
        </div>
        <Button type="button" className="w-full bg-emerald-700 hover:bg-emerald-800 text-white" onClick={onOpen}>
          Open store
        </Button>
      </CardContent>
    </Card>
  );
}
